import { useState, useEffect } from "react";
import { useAuth, api } from "@/context/AuthContext";
import MaterialAutocomplete from "@/components/MaterialAutocomplete";
import MaterialUsageHistory from "@/components/MaterialUsageHistory";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Package, Pencil, Trash2, X, CheckCircle2, Mic, MicOff, WarehouseIcon } from "lucide-react";
import { MaterialFormFields, emptyMaterial, computeMaterialCost, materialFromInventory } from "./MaterialFormFields";
import { startVoiceRecognition } from "./orderHelpers";
import { QuickAddMaterialDialog } from "./QuickAddMaterialDialog";

const deductStock = async (materialien) => {
  const items = materialien.filter(m => m.material_id).map(m => ({ material_id: m.material_id, menge: parseFloat(m.anzahl) || 1 }));
  if (items.length === 0) return;
  try {
    const res = await api.post("/api/materials/deduct-for-order", { items });
    if (res.data.warnings && res.data.warnings.length > 0) {
      res.data.warnings.forEach(w => toast.warning(w));
    }
  } catch (err) { /* silent - stock deduction is best-effort */ }
};

const returnStock = async (materialien) => {
  const items = materialien.filter(m => m.material_id).map(m => ({ material_id: m.material_id, menge: parseFloat(m.anzahl) || 1 }));
  if (items.length === 0) return;
  try {
    await api.post("/api/materials/return-for-order", { items });
  } catch (err) { /* silent */ }
};

// Diff old vs new materials by material_id+anzahl, dispatch deduct/return for the difference
const reconcileStockOnEdit = async (oldMaterialien = [], newMaterialien = []) => {
  const keyOf = (m) => `${m.material_id || ""}|${m.anzahl || ""}`;
  const oldMap = new Map();
  oldMaterialien.forEach(m => { if (m.material_id) oldMap.set(keyOf(m), (oldMap.get(keyOf(m)) || 0) + 1); });
  const newMap = new Map();
  newMaterialien.forEach(m => { if (m.material_id) newMap.set(keyOf(m), (newMap.get(keyOf(m)) || 0) + 1); });
  const toDeduct = [];
  const toReturn = [];
  // Items in new but missing/decreased in old → deduct
  newMaterialien.forEach(m => {
    if (!m.material_id) return;
    const k = keyOf(m);
    const oldCount = oldMap.get(k) || 0;
    const newCount = newMap.get(k) || 0;
    if (newCount > oldCount) {
      toDeduct.push(m);
      newMap.set(k, newCount - 1);
    }
  });
  // Items in old but missing/decreased in new → return
  oldMaterialien.forEach(m => {
    if (!m.material_id) return;
    const k = keyOf(m);
    const oldCount = oldMap.get(k) || 0;
    const newCount = newMap.get(k) || 0;
    if (oldCount > newCount) {
      toReturn.push(m);
      oldMap.set(k, oldCount - 1);
    }
  });
  if (toDeduct.length) await deductStock(toDeduct);
  if (toReturn.length) await returnStock(toReturn);
};

const formatErrorDetail = (err) => {
  const detail = err?.response?.data?.detail;
  if (!detail) return "Fehler beim Speichern";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map(d => d?.msg || JSON.stringify(d)).join(", ");
  }
  return "Fehler beim Speichern";
};

export const TaskFormDialog = ({ open, onOpenChange, orderId, editTask, onSuccess }) => {
  const { user } = useAuth();
  const isEdit = !!editTask;
  const isAdmin = user?.role === "admin";
  const [taskForm, setTaskForm] = useState({
    description: editTask?.description || "",
    materialien: editTask?.materialien || [],
    time_minutes: editTask?.time_minutes || 0,
  });
  const [taskMaterial, setTaskMaterial] = useState(emptyMaterial());
  const [editingMaterialIndex, setEditingMaterialIndex] = useState(-1);
  const [isListening, setIsListening] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [pendingMaterial, setPendingMaterial] = useState(null);
  // Snapshot of materials when dialog opened, used to detect Lager-relevant edits
  const [originalMaterials, setOriginalMaterials] = useState([]);
  const [lagerSyncDialog, setLagerSyncDialog] = useState(null);

  // Sync form state when dialog opens or editTask changes
  // (useState init only runs once per mount - dialog is permanently mounted in parent)
  useEffect(() => {
    if (open) {
      const initial = editTask?.materialien || [];
      setTaskForm({
        description: editTask?.description || "",
        materialien: initial,
        time_minutes: editTask?.time_minutes || 0,
      });
      // Deep-clone snapshot so later mutations don't affect comparison
      setOriginalMaterials(JSON.parse(JSON.stringify(initial)));
      setTaskMaterial(emptyMaterial());
      setEditingMaterialIndex(-1);
    }
  }, [open, editTask]);

  // Reset on close
  const handleOpenChange = (val) => {
    if (!val) {
      setTaskMaterial(emptyMaterial());
      setEditingMaterialIndex(-1);
    }
    onOpenChange(val);
  };

  const addMaterial = () => {
    if (!taskMaterial.bezeichnung.trim()) { toast.error("Materialbezeichnung eingeben"); return; }
    const mat = computeMaterialCost(taskMaterial);
    const updated = [...taskForm.materialien];
    if (editingMaterialIndex >= 0) {
      updated[editingMaterialIndex] = mat;
      setEditingMaterialIndex(-1);
    } else {
      updated.push(mat);
    }
    setTaskForm({ ...taskForm, materialien: updated });
    
    // If admin and no material_id → offer to add to inventory.
    // Fires both when adding a NEW material and when editing an existing one
    // whose bezeichnung was changed to something not in the inventory.
    if (isAdmin && !mat.material_id) {
      setPendingMaterial(mat);
    }
    setTaskMaterial(emptyMaterial());
  };

  const handleQuickAddSaved = (savedMaterial) => {
    // Update the material that was just added/edited with full data from the new Lager item.
    // Match by bezeichnung + missing material_id so it works for both add-new and edit-existing flows.
    if (pendingMaterial) {
      setTaskForm(prev => ({
        ...prev,
        materialien: prev.materialien.map((m) => {
          if (m.bezeichnung !== pendingMaterial.bezeichnung || m.material_id) return m;
          const anzahl = parseFloat(m.anzahl) || 0;
          const einzelpreis = savedMaterial.verkaufspreis ?? m.einzelpreis ?? 0;
          return {
            ...m,
            material_id: savedMaterial.id,
            // Adopt the bezeichnung that was actually saved to Lager. The user may have
            // refined it inside the QuickAdd dialog (e.g. "Ölfilter Audi" → "Ölfilter Test").
            bezeichnung: savedMaterial.bezeichnung || m.bezeichnung,
            artikelnummer: savedMaterial.artikelnummer || m.artikelnummer || "",
            einkaufspreis: savedMaterial.einkaufspreis ?? 0,
            marge_prozent: savedMaterial.marge_prozent ?? 0,
            einzelpreis,
            kosten: parseFloat((anzahl * einzelpreis).toFixed(2)),
            einheit: savedMaterial.einheit || m.einheit || "Stück",
          };
        })
      }));
      // Also keep snapshot in sync so the post-save Lager-sync prompt doesn't fire
      // for fields the user already saved through QuickAdd.
      setOriginalMaterials((prev) => {
        // Replace any matching original entry so detectLagerChanges sees no diff
        return prev.map((m) => m.bezeichnung === pendingMaterial.bezeichnung && !m.material_id
          ? { ...m, material_id: savedMaterial.id, bezeichnung: savedMaterial.bezeichnung || m.bezeichnung }
          : m);
      });
    }
    setPendingMaterial(null);
  };

  const editMaterial = (index) => {
    const m = taskForm.materialien[index];
    setTaskMaterial({
      bezeichnung: m.bezeichnung || "", anzahl: m.anzahl || "", einheit: m.einheit || "Stück",
      artikelnummer: m.artikelnummer || "", einzelpreis: m.einzelpreis || 0,
      einkaufspreis: m.einkaufspreis || 0, marge_prozent: m.marge_prozent || 0,
      kosten: m.kosten || 0, material_id: m.material_id || "",
      flaeche_laenge: m.flaeche_laenge || "", flaeche_breite: m.flaeche_breite || "",
      flaeche_einheit: m.flaeche_einheit || "cm",
    });
    setEditingMaterialIndex(index);
  };

  const removeMaterial = (index) => {
    // Stock auto-reconciles on the backend when the task is saved (apply_material_diff)
    setTaskForm({ ...taskForm, materialien: taskForm.materialien.filter((_, i) => i !== index) });
    if (editingMaterialIndex === index) { setEditingMaterialIndex(-1); setTaskMaterial(emptyMaterial()); }
  };

  // Detect materials with material_id whose Lager-relevant fields changed since dialog opened.
  // Returns array of { material_id, bezeichnung, fields-to-update } for the prompt.
  const detectLagerChanges = (originals, current) => {
    const origMap = new Map();
    originals.forEach((m) => { if (m.material_id) origMap.set(m.material_id, m); });
    const changes = [];
    const seen = new Set();
    current.forEach((m) => {
      if (!m.material_id || seen.has(m.material_id)) return;
      seen.add(m.material_id);
      const orig = origMap.get(m.material_id);
      if (!orig) return; // newly linked, not from snapshot
      const fields = {};
      if ((m.bezeichnung || "") !== (orig.bezeichnung || "")) fields.bezeichnung = m.bezeichnung;
      if ((m.artikelnummer || "") !== (orig.artikelnummer || "")) fields.artikelnummer = m.artikelnummer;
      if (parseFloat(m.einkaufspreis || 0) !== parseFloat(orig.einkaufspreis || 0)) fields.einkaufspreis = parseFloat(m.einkaufspreis || 0);
      if (parseFloat(m.einzelpreis || 0) !== parseFloat(orig.einzelpreis || 0)) fields.verkaufspreis = parseFloat(m.einzelpreis || 0);
      if ((m.einheit || "") !== (orig.einheit || "")) fields.einheit = m.einheit;
      if (Object.keys(fields).length > 0) {
        changes.push({ material_id: m.material_id, bezeichnung: m.bezeichnung, fields });
      }
    });
    return changes;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!orderId) return;
    try {
      if (isEdit) {
        await api.put(`/api/work-orders/${orderId}/tasks/${editTask.id}/full`, {
          description: taskForm.description,
          materialien: taskForm.materialien,
          time_minutes: taskForm.time_minutes,
        });
        // Stock auto-reconciles on the backend (apply_material_diff)
        toast.success("Aufgabe aktualisiert");
      } else {
        await api.post(`/api/work-orders/${orderId}/tasks`, {
          description: taskForm.description,
          materialien: taskForm.materialien,
          time_minutes: taskForm.time_minutes,
        });
        toast.success("Aufgabe hinzugefügt");
        // Stock auto-reserves on the backend
      }
      // Admin only: ask whether Lager-linked changes should propagate to inventory
      const changes = isAdmin ? detectLagerChanges(originalMaterials, taskForm.materialien) : [];
      if (changes.length > 0) {
        setLagerSyncDialog({ changes });
        // Don't close yet - let the dialog handler close after answer
      } else {
        onOpenChange(false);
        onSuccess();
      }
    } catch (err) {
      toast.error(formatErrorDetail(err));
    }
  };

  const applyLagerSync = async (yes) => {
    if (yes && lagerSyncDialog?.changes) {
      for (const change of lagerSyncDialog.changes) {
        try {
          await api.post(`/api/materials/${change.material_id}/sync-from-order`, change.fields);
        } catch (err) {
          toast.error(`Lager-Update fehlgeschlagen: ${change.bezeichnung}`);
        }
      }
      if (lagerSyncDialog.changes.length > 0) {
        toast.success(`${lagerSyncDialog.changes.length} Lagerartikel aktualisiert`);
      }
    }
    setLagerSyncDialog(null);
    onOpenChange(false);
    onSuccess();
  };

  const voiceDescription = () => {
    startVoiceRecognition('taskFormDescription', (v) => setTaskForm(f => ({ ...f, description: v })), taskForm.description, setIsListening, setActiveField);
  };

  const voiceMaterialBez = () => {
    startVoiceRecognition('materialBezeichnung', (v) => setTaskMaterial(m => ({ ...m, bezeichnung: v })), taskMaterial.bezeichnung, setIsListening, setActiveField);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" data-testid={isEdit ? "edit-task-dialog" : "task-dialog"}>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Barlow Condensed' }} className="text-xl">
            {isEdit ? "Aufgabe bearbeiten" : "Neue Aufgabe"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Aufgabe mit Beschreibung, geplanter Zeit und Materialien
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>Beschreibung *</Label>
            <div className="flex gap-2">
              <Input
                value={taskForm.description}
                onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                placeholder="z.B. Ölwechsel"
                required
                data-testid={isEdit ? "edit-task-description-input" : "task-description-input"}
                className="flex-1"
              />
              <Button type="button" variant={isListening && activeField === 'taskFormDescription' ? "destructive" : "outline"} size="icon" onClick={voiceDescription} className="flex-shrink-0">
                {isListening && activeField === 'taskFormDescription' ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Geschätzte Zeit (Minuten)</Label>
            <Input
              type="number" min="0" value={taskForm.time_minutes}
              onChange={(e) => setTaskForm({ ...taskForm, time_minutes: parseInt(e.target.value) || 0 })}
              className="w-32" data-testid={isEdit ? "edit-task-time-input" : "task-time-input"}
            />
          </div>

          {/* Materials Section */}
          <div className="bg-muted/30 p-3 rounded-md space-y-3">
            <Label className="text-sm font-medium flex items-center gap-1">
              <Package className="w-4 h-4" /> Materialien
            </Label>
            {taskForm.materialien.length > 0 && (
              <div className="space-y-1">
                {taskForm.materialien.map((m, idx) => (
                  <div key={idx} className="text-xs bg-white p-2 rounded border flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{m.bezeichnung}</span>
                    {m.anzahl && <span className="text-muted-foreground">{m.anzahl} {m.einheit}</span>}
                    {m.artikelnummer && <span className="text-muted-foreground">Art.Nr: {m.artikelnummer}</span>}
                    {m.kosten > 0 && <span className="text-green-700">{m.kosten.toFixed(2)} €</span>}
                    <div className="ml-auto flex gap-1">
                      <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => editMaterial(idx)}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => removeMaterial(idx)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-2">
              <div className="flex gap-1 items-center">
                <div className="flex-1">
                  <MaterialAutocomplete
                    value={taskMaterial.bezeichnung}
                    onChange={(v) => setTaskMaterial({ ...taskMaterial, bezeichnung: v, material_id: "" })}
                    onSelect={(m) => setTaskMaterial(materialFromInventory(m, taskMaterial))}
                    placeholder="Material suchen..."
                  />
                </div>
                <Button type="button" variant={isListening && activeField === 'materialBezeichnung' ? "destructive" : "ghost"} size="icon" className="h-8 w-8 flex-shrink-0" onClick={voiceMaterialBez}>
                  <Mic className="w-3 h-3" />
                </Button>
              </div>
              <MaterialFormFields material={taskMaterial} setMaterial={setTaskMaterial} testIdPrefix={isEdit ? "edit-task-mat" : "add-task-mat"} />
              {taskMaterial.material_id && (
                <MaterialUsageHistory
                  materialId={taskMaterial.material_id}
                  materialName={taskMaterial.bezeichnung}
                  currentOrderId={orderId}
                  defaultOpen={true}
                />
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant={editingMaterialIndex >= 0 ? "default" : "secondary"} size="sm" className="flex-1 text-xs" onClick={addMaterial}>
                {editingMaterialIndex >= 0 ? (<><CheckCircle2 className="w-3 h-3 mr-1" />Material aktualisieren</>) : (<><Plus className="w-3 h-3 mr-1" />Material hinzufügen</>)}
              </Button>
              {editingMaterialIndex >= 0 && (
                <Button type="button" variant="outline" size="sm" className="text-xs" onClick={() => { setEditingMaterialIndex(-1); setTaskMaterial(emptyMaterial()); }}>
                  Abbrechen
                </Button>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
            <Button type="submit" className="btn-primary" data-testid={isEdit ? "edit-task-submit-button" : "task-submit-button"}>
              {isEdit ? "Speichern" : "Hinzufügen"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>

    {/* "Ins Lager übernehmen?" Prompt */}
    {pendingMaterial && !quickAddOpen && (
      <Dialog open={true} onOpenChange={() => setPendingMaterial(null)}>
        <DialogContent className="sm:max-w-[400px]" data-testid="lager-prompt-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Barlow Condensed" }}>Ins Lager übernehmen?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            <strong>"{pendingMaterial.bezeichnung}"</strong> ist nicht im Lager vorhanden. Möchtest du es als Lagerartikel anlegen?
          </p>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setPendingMaterial(null)} data-testid="lager-prompt-no">Nein</Button>
            <Button onClick={() => setQuickAddOpen(true)} className="gap-2" data-testid="lager-prompt-yes">
              <Package className="w-4 h-4" /> Ja, ins Lager
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )}

    {/* Quick-Add Material to Inventory Dialog */}
    <QuickAddMaterialDialog
      open={quickAddOpen}
      onOpenChange={(v) => { setQuickAddOpen(v); if (!v) setPendingMaterial(null); }}
      prefill={pendingMaterial}
      onSaved={handleQuickAddSaved}
    />

    {/* Sync Order → Lager prompt: appears after save if user changed price/name/etc. of a Lager-linked material */}
    {lagerSyncDialog && (
      <Dialog open onOpenChange={() => applyLagerSync(false)}>
        <DialogContent className="sm:max-w-md" data-testid="lager-sync-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }} className="text-xl">
              Änderungen ins Lager übernehmen?
            </DialogTitle>
            <DialogDescription className="sr-only">
              Frage, ob die im Auftrag geänderten Werte auch im Lagerartikel aktualisiert werden sollen.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Du hast {lagerSyncDialog.changes.length === 1 ? "einen Artikel" : `${lagerSyncDialog.changes.length} Artikel`} geändert. Soll {lagerSyncDialog.changes.length === 1 ? "die Änderung" : "diese Änderungen"} auch im Lager übernommen werden?
          </p>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {lagerSyncDialog.changes.map((c) => (
              <div key={c.material_id} className="bg-muted/40 rounded p-2 text-sm">
                <div className="font-medium">{c.bezeichnung}</div>
                <div className="text-xs text-muted-foreground space-x-2">
                  {Object.entries(c.fields).map(([k, v]) => (
                    <span key={k} className="inline-block">
                      {k}: <strong>{typeof v === "number" ? v.toFixed(2) : v}</strong>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => applyLagerSync(false)} data-testid="lager-sync-no">Nein</Button>
            <Button onClick={() => applyLagerSync(true)} className="gap-2" data-testid="lager-sync-yes">
              <Package className="w-4 h-4" /> Ja, im Lager aktualisieren
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )}
    </>
  );
};
